import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  Image,
  Icon,
  Avatar,
  CircularProgress,
  SimpleGrid,
  Divider,
} from "@chakra-ui/react";
import { IoIosArrowBack } from "react-icons/io";
import {
  IoTimeOutline,
  IoAlertCircleOutline,
  IoCalendarOutline,
  IoCardOutline,
  IoReceiptOutline,
  IoStorefrontOutline,
  IoMailOutline,
  IoCutOutline,
} from "react-icons/io5";

import {
  useBookingDetails,
  formatDurationHM,
  isIOS,
  isMobile,
  StatusStyle,
  BookingServiceItem,
} from "../hooks/useBookingDetails";
import { OpenInAppBanner } from "../components/OpenInAppBanner";
import LogoIcon from "../assets/images/logoIcon.png";
import LogoText from "../assets/images/logoText.png";

const cardStyle = {
  bg: "white",
  borderRadius: "24px",
  boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.06)",
  border: "1px solid #EEF2F2",
} as const;

// A small coloured status pill.
const StatusPill = ({
  label,
  style,
}: {
  label: string;
  style: StatusStyle;
}) => (
  <Flex
    align="center"
    gap="7px"
    bg={style.bg}
    borderRadius="full"
    px="12px"
    py="5px"
    flexShrink={0}
  >
    <Box w="8px" h="8px" borderRadius="full" bg={style.dot} />
    <Text fontSize="13px" fontWeight={700} color={style.color}>
      {label}
    </Text>
  </Flex>
);

// A labelled key/value row with an icon, used in the info card.
const InfoRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
}) => (
  <Flex gap="14px" align="flex-start">
    <Flex
      w="40px"
      h="40px"
      borderRadius="12px"
      bg="#F4F8F8"
      align="center"
      justify="center"
      flexShrink={0}
      color="#267684"
      fontSize="20px"
    >
      {icon}
    </Flex>
    <Box flex={1} minW={0}>
      <Text fontSize="13px" color="#9BA3A3" fontWeight={600} mb="2px">
        {label}
      </Text>
      <Text
        fontSize="15px"
        color="#19202D"
        fontFamily="RedHatDisplay-Bold"
        wordBreak="break-word"
      >
        {value && String(value).trim() ? value : "N/A"}
      </Text>
    </Box>
  </Flex>
);

const PayItem = ({
  label,
  value,
  highlight,
}: {
  label: string;
  value?: number | string | null;
  highlight?: boolean;
}) => (
  <Box bg="#F7FAFA" borderRadius="16px" p="16px" border="1px solid #EEF2F2">
    <Text fontSize="12px" color="#9BA3A3" fontWeight={600} mb="6px">
      {label}
    </Text>
    <Text
      fontSize="18px"
      color={highlight ? "#267684" : "#19202D"}
      fontFamily="RedHatDisplay-Bold"
    >
      {value != null && value !== "" ? `£${value}` : "—"}
    </Text>
  </Box>
);

// A single service in the booking's services list.
const ServiceCard = ({ service }: { service: BookingServiceItem }) => (
  <Box border="1px solid #EEF2F2" borderRadius="18px" p={{ base: "16px", md: "20px" }}>
    <Flex justify="space-between" align="flex-start" gap="12px" mb="14px">
      <Flex gap="12px" align="flex-start" minW={0}>
        <Flex
          w="44px"
          h="44px"
          borderRadius="12px"
          bg="linear-gradient(160deg, #DBF1F2 0%, #C7EBE6 100%)"
          align="center"
          justify="center"
          flexShrink={0}
          color="#267684"
          fontSize="22px"
        >
          <IoCutOutline />
        </Flex>
        <Box minW={0}>
          <Heading
            fontSize="16px"
            color="#19202D"
            fontFamily="RedHatDisplay-Bold"
            mb="2px"
          >
            {service.name}
          </Heading>
          <Text fontSize="13px" color="#9BA3A3" fontWeight={600}>
            with {service.stylistName}
          </Text>
        </Box>
      </Flex>
      <Text
        fontSize="18px"
        color="#267684"
        fontFamily="RedHatDisplay-Bold"
        whiteSpace="nowrap"
      >
        £{service.price ?? "—"}
      </Text>
    </Flex>

    <Flex gap={{ base: "12px", md: "24px" }} flexWrap="wrap" mb="14px">
      <Flex align="center" gap="7px" color="#4F4F51">
        <IoCalendarOutline size={16} color="#9BA3A3" />
        <Text fontSize="13px" fontWeight={600}>
          {service.dateLabel || "N/A"}
        </Text>
      </Flex>
      <Flex align="center" gap="7px" color="#4F4F51">
        <IoTimeOutline size={16} color="#9BA3A3" />
        <Text fontSize="13px" fontWeight={600}>
          {service.timeRange}
        </Text>
      </Flex>
      {service.duration != null && (
        <Flex align="center" gap="7px" color="#4F4F51">
          <IoTimeOutline size={16} color="#9BA3A3" />
          <Text fontSize="13px" fontWeight={600}>
            {formatDurationHM(service.duration)}
          </Text>
        </Flex>
      )}
    </Flex>

    <Flex gap="8px" flexWrap="wrap">
      <StatusPill label={service.status} style={service.statusStyle} />
      {service.availed && service.availed !== service.status && (
        <Box bg="#F4F8F8" borderRadius="full" px="12px" py="5px">
          <Text fontSize="13px" fontWeight={700} color="#267684">
            {service.availed}
          </Text>
        </Box>
      )}
    </Flex>
  </Box>
);

export const BookingDetails = () => {
  const {
    navigate,
    jwt,
    loading,
    error,
    deepLink,
    bookingRef,
    dateOfBooking,
    status,
    statusStyle,
    paymentStatus,
    paymentStatusStyle,
    paymentMethod,
    serviceTypeLabel,
    customerName,
    customerEmail,
    dayNum,
    monthShort,
    timeRange,
    services,
    gross,
    discount,
    total,
    paid,
    due,
  } = useBookingDetails();

  if (!jwt) return null;

  return (
    <Box
      display="flex"
      flexDir="column"
      minH="100vh"
      bg="linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 40%, #FFF7F3 70%, #D8F1F2 100%)"
      pb={isMobile ? "80px" : "40px"}
    >
      <Flex
        boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
        minH="72px"
        px={{ base: "20px", md: "10%" }}
        align="center"
        justify="space-between"
        bg="white"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Flex align="center" gap="16px">
          <Icon
            fontSize={24}
            color="#19202D"
            cursor="pointer"
            onClick={() => navigate("/")}
          >
            <IoIosArrowBack />
          </Icon>

          <Image
            src={LogoIcon}
            alt="Book-d Logo"
            h="32px"
            w="19px"
            cursor="pointer"
            onClick={() => navigate("/")}
          />
        </Flex>

        <Heading
          fontSize={{ base: "18px", md: "20px" }}
          color="#19202D"
          fontFamily="RedHatDisplay-Bold"
        >
          Booking Details
        </Heading>
      </Flex>

      {isMobile && <OpenInAppBanner deepLink={deepLink} isIOS={isIOS} />}

      <Flex
        flex={1}
        align="flex-start"
        justify="center"
        px={{ base: "16px", md: "20px" }}
        py={{ base: "24px", md: "40px" }}
      >
        <Box w="100%" maxW="960px">
          {loading ? (
            <Flex py="120px" align="center" justify="center">
              <CircularProgress isIndeterminate color="#FF8E3C" size="60px" />
            </Flex>
          ) : error ? (
            <Flex
              {...cardStyle}
              flexDir="column"
              align="center"
              textAlign="center"
              p={{ base: "40px 24px", md: "56px 40px" }}
              maxW="520px"
              mx="auto"
            >
              <Flex
                w="72px"
                h="72px"
                borderRadius="full"
                bg="#FDECEC"
                align="center"
                justify="center"
                mb="20px"
                color="#D64545"
                fontSize="36px"
              >
                <IoAlertCircleOutline />
              </Flex>
              <Heading
                fontSize={{ base: "22px", md: "26px" }}
                color="#19202D"
                fontFamily="RedHatDisplay-Bold"
                mb="10px"
              >
                Unable to load booking
              </Heading>
              <Text fontSize="15px" color="#4F4F51" lineHeight="24px" mb="28px">
                {error}
              </Text>
              <Button
                bg="#FF8E3C"
                _hover={{ bg: "#e07a30" }}
                borderRadius="30px"
                color="white"
                h="50px"
                px="36px"
                fontSize="16px"
                fontFamily="RedHatDisplay-Bold"
                onClick={() => navigate("/")}
              >
                Back to Home
              </Button>
            </Flex>
          ) : (
            <>
              {/* ---- Summary header card ---- */}
              <Flex
                {...cardStyle}
                p={{ base: "20px", md: "28px 32px" }}
                mb={{ base: "16px", md: "24px" }}
                align="center"
                justify="space-between"
                gap="20px"
                flexWrap="wrap"
              >
                <Flex align="center" gap={{ base: "16px", md: "24px" }}>
                  <Flex
                    flexDir="column"
                    align="center"
                    justify="center"
                    bg="linear-gradient(160deg, #DBF1F2 0%, #C7EBE6 100%)"
                    borderRadius="18px"
                    w={{ base: "64px", md: "76px" }}
                    h={{ base: "64px", md: "76px" }}
                    flexShrink={0}
                  >
                    <Text
                      fontSize={{ base: "26px", md: "30px" }}
                      color="#19202D"
                      fontFamily="RedHatDisplay-Bold"
                      lineHeight={1}
                    >
                      {dayNum}
                    </Text>
                    <Text
                      fontSize="13px"
                      color="#267684"
                      fontWeight={700}
                      textTransform="uppercase"
                    >
                      {monthShort}
                    </Text>
                  </Flex>

                  <Box>
                    {bookingRef && (
                      <Text
                        fontSize="13px"
                        color="#9BA3A3"
                        fontWeight={700}
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb="4px"
                      >
                        Ref #{bookingRef}
                      </Text>
                    )}
                    <Flex align="center" gap="10px">
                      <IoTimeOutline size={22} color="#267684" />
                      <Text
                        fontSize={{ base: "18px", md: "22px" }}
                        color="#19202D"
                        fontFamily="RedHatDisplay-Bold"
                      >
                        {timeRange}
                      </Text>
                    </Flex>
                  </Box>
                </Flex>

                <Flex
                  flexDir="column"
                  align={{ base: "flex-start", sm: "flex-end" }}
                  gap="8px"
                >
                  <StatusPill label={status} style={statusStyle} />
                  {paymentStatus && (
                    <StatusPill
                      label={paymentStatus}
                      style={paymentStatusStyle}
                    />
                  )}
                  <Box bg="#DBF1F2" borderRadius="full" px="12px" py="5px">
                    <Text fontSize="13px" fontWeight={700} color="#267684">
                      {serviceTypeLabel}
                    </Text>
                  </Box>
                </Flex>
              </Flex>

              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing={{ base: "16px", md: "24px" }}
              >
                {/* ---- Services card (full width) ---- */}
                <Box
                  {...cardStyle}
                  p={{ base: "22px", md: "28px" }}
                  gridColumn={{ md: "1 / -1" }}
                >
                  <Flex justify="space-between" align="center" mb="18px">
                    <Heading
                      fontSize="13px"
                      color="#9BA3A3"
                      fontFamily="RedHatDisplay-Bold"
                      textTransform="uppercase"
                      letterSpacing="0.6px"
                    >
                      Services
                    </Heading>
                    <Text fontSize="13px" color="#9BA3A3" fontWeight={700}>
                      {services.length}{" "}
                      {services.length === 1 ? "service" : "services"}
                    </Text>
                  </Flex>

                  <Flex flexDir="column" gap="14px">
                    {services.length > 0 ? (
                      services.map((s) => (
                        <ServiceCard key={s.id} service={s} />
                      ))
                    ) : (
                      <Text fontSize="14px" color="#9BA3A3">
                        No services found for this booking.
                      </Text>
                    )}
                  </Flex>
                </Box>

                {/* ---- Customer card ---- */}
                <Box {...cardStyle} p={{ base: "22px", md: "28px" }}>
                  <Heading
                    fontSize="13px"
                    color="#9BA3A3"
                    fontFamily="RedHatDisplay-Bold"
                    textTransform="uppercase"
                    letterSpacing="0.6px"
                    mb="18px"
                  >
                    Customer
                  </Heading>

                  <Flex align="center" gap="12px" mb="20px">
                    <Avatar
                      size="md"
                      name={customerName}
                      bg="#C7E86B"
                      color="#19202D"
                    />
                    <Box minW={0}>
                      <Text
                        fontSize="16px"
                        color="#19202D"
                        fontFamily="RedHatDisplay-Bold"
                      >
                        {customerName}
                      </Text>
                      {customerEmail && (
                        <Flex align="center" gap="6px" color="#9BA3A3">
                          <IoMailOutline size={15} color="#9BA3A3" />
                          <Text
                            fontSize="13px"
                            fontWeight={600}
                            wordBreak="break-all"
                          >
                            {customerEmail}
                          </Text>
                        </Flex>
                      )}
                    </Box>
                  </Flex>

                  <Divider mb="18px" borderColor="#EEF2F2" />

                  <Flex flexDir="column" gap="16px">
                    <InfoRow
                      icon={<IoStorefrontOutline />}
                      label="Service Type"
                      value={serviceTypeLabel}
                    />
                    <InfoRow
                      icon={<IoCalendarOutline />}
                      label="Booked On"
                      value={dateOfBooking}
                    />
                  </Flex>
                </Box>

                {/* ---- Payment card ---- */}
                <Box {...cardStyle} p={{ base: "22px", md: "28px" }}>
                  <Heading
                    fontSize="13px"
                    color="#9BA3A3"
                    fontFamily="RedHatDisplay-Bold"
                    textTransform="uppercase"
                    letterSpacing="0.6px"
                    mb="18px"
                  >
                    Payment
                  </Heading>

                  <Flex flexDir="column" gap="16px" mb="20px">
                    <InfoRow
                      icon={<IoCardOutline />}
                      label="Payment Method"
                      value={paymentMethod}
                    />
                    <InfoRow
                      icon={<IoReceiptOutline />}
                      label="Payment Status"
                      value={paymentStatus || "N/A"}
                    />
                  </Flex>

                  <SimpleGrid columns={2} spacing="12px">
                    <PayItem label="Subtotal" value={gross} />
                    <PayItem label="Discount" value={discount} />
                    <PayItem label="Paid" value={paid} highlight />
                    <PayItem label="Balance Due" value={due} />
                  </SimpleGrid>

                  <Flex
                    justify="space-between"
                    align="center"
                    mt="18px"
                    pt="16px"
                    borderTop="1px solid #EEF2F2"
                  >
                    <Text
                      fontSize="15px"
                      color="#19202D"
                      fontFamily="RedHatDisplay-Bold"
                    >
                      Total
                    </Text>
                    <Text
                      fontSize="22px"
                      color="#267684"
                      fontFamily="RedHatDisplay-Bold"
                    >
                      {total != null ? `£${total}` : "—"}
                    </Text>
                  </Flex>
                </Box>
              </SimpleGrid>

              <Flex
                align="center"
                justify="center"
                gap="6px"
                mt="40px"
                opacity={0.5}
              >
                <Text fontSize="13px" color="#4F4F51">
                  Powered by
                </Text>
                <Image src={LogoText} h="18px" objectFit="contain" />
              </Flex>
            </>
          )}
        </Box>
      </Flex>
    </Box>
  );
};
